import React, {forwardRef, useImperativeHandle, useLayoutEffect, useRef} from 'react';
import {ScriptCompletionGhost} from './ScriptCompletionGhost';
import type {ScriptBlockData} from './ScriptBlock';
import {revisionStyledRuns,type ScriptRevisions,type RevisionDisplayRange} from '../../lib/scriptRevisions';
import {observedTextEdit,scriptFontSize,type ScriptTextEdit} from '../../lib/scriptRichText';
import type {DraftLine} from '../../lib/scriptDraftLayout';

export interface RichScriptTextHandle {
    readonly selectionStart: number;
    readonly selectionEnd: number;
    readonly style: CSSStyleDeclaration;
    readonly scrollHeight: number;
    focus(): void;
    getBoundingClientRect(): DOMRect;
    setSelectionRange(start: number, end: number): void;
}
function selectionOffsets(root: HTMLElement): [number, number] | undefined {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return;
    const prefix = range.cloneRange(); prefix.selectNodeContents(root); prefix.setEnd(range.startContainer, range.startOffset);
    return [prefix.toString().length, prefix.toString().length + range.toString().length];
}
function select(root: HTMLElement, start: number, end: number, preferNext=false): void {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = []; let node;
    while ((node = walker.nextNode())) nodes.push(node as Text);
    const point = (offset: number,next=false): [Node, number] => {
        const empty=root.querySelector(`[data-source-start="${offset}"][data-source-end="${offset}"]`);
        if(empty)return [empty,0];
        for (const text of nodes) {
            if (offset < text.length || offset===text.length&&!next) return [text, offset];
            offset -= text.length;
        }
        return [root, root.childNodes.length];
    };
    const range = document.createRange(); range.setStart(...point(start,preferNext)); range.setEnd(...point(end,preferNext&&start===end));
    const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(range);
}
interface Props {
    block: ScriptBlockData; readOnly: boolean; className: string; placeholder?: string; ariaLabel?:string;
    style?:React.CSSProperties;
    lines?:readonly DraftLine[];
    originX?:number;
    onBlur?:()=>void;
    completion?:{id:string;index:number};
    suggestedSuffix?:string;
    revisions?:ScriptRevisions;
    revisionRanges?:readonly RevisionDisplayRange[];
    onChange(value: string,edit?:ScriptTextEdit): void; onFocus(): void; onKeyDown(event: React.KeyboardEvent): void;
}

/** Browser selection and composition stay local. Only plain text enters the
 * document; pasted HTML is never interpreted as screenplay markup. */
export const RichScriptText = forwardRef<RichScriptTextHandle, Props>(({block, readOnly, className, style,lines,originX=0, placeholder, ariaLabel, revisions, revisionRanges, onChange, onFocus,onBlur,completion,suggestedSuffix, onKeyDown}, ref) => {
    const root = useRef<HTMLDivElement>(null), composing = useRef(false);
    const pendingSelection = useRef<[number, number] | undefined>(undefined);
    const inputRange=useRef<{before:string;start:number;end:number}|undefined>(undefined);
    const compositionRange=useRef<{before:string;start:number;end:number}|undefined>(undefined);
    const painted=useRef('');
    const lastEmitted=useRef(block.content);
    useLayoutEffect(()=>{
        const element=root.current;if(!element)return;
        const capture=(event:InputEvent)=>{
            inputRange.current=undefined;if(readOnly||composing.current||event.isComposing)return;
            const before=element.textContent?.replace(/\r\n/g,'\n')??'';
            const target=event.getTargetRanges?.()[0];
            let offsets=selectionOffsets(element);
            if(target&&element.contains(target.startContainer)&&element.contains(target.endContainer)){
                const prefix=document.createRange();prefix.selectNodeContents(element);prefix.setEnd(target.startContainer,target.startOffset);
                const range=document.createRange();range.setStart(target.startContainer,target.startOffset);range.setEnd(target.endContainer,target.endOffset);
                offsets=[prefix.toString().length,prefix.toString().length+range.toString().length];
            } else if(offsets&&offsets[0]===offsets[1]&&!event.inputType.startsWith('insert'))return;
            if(offsets)inputRange.current={before,start:offsets[0],end:offsets[1]};
        };
        element.addEventListener('beforeinput',capture);
        return ()=>element.removeEventListener('beforeinput',capture);
    },[readOnly]);
    const emitChange=(range=inputRange.current)=>{
        inputRange.current=undefined;
        const value=root.current?.textContent?.replace(/\r\n/g,'\n')??'';
        if(readOnly)return;
        // Some browsers send a final input after compositionend. Commit once;
        // cancelled composition still needs its temporary DOM normalized.
        if(value===lastEmitted.current){
            if(value===block.content){painted.current='';paint();}
            return;
        }
        lastEmitted.current=value;
        onChange(value,range?observedTextEdit(range.before,value,range.start,range.end):undefined);
    };
    const paint = () => {
        const element = root.current; if (!element || composing.current) return;
        lastEmitted.current=block.content;
        const styledRuns=revisionStyledRuns(block,revisions,revisionRanges);
        const signature=JSON.stringify({runs:styledRuns,lines,originX});
        // Focus/menu renders must not replace the DOM under an in-flight click.
        if(painted.current===signature && element.textContent===block.content && !pendingSelection.current)return;
        const offsets = pendingSelection.current ?? selectionOffsets(element);
        const currentSelection=window.getSelection();
        const anchor=currentSelection?.rangeCount?currentSelection.getRangeAt(0).startContainer:undefined;
        const anchorRow=(anchor instanceof Element?anchor:anchor?.parentElement)?.closest<HTMLElement>('[data-draft-line]');
        const preferNext=Boolean(anchorRow&&offsets&&Number(anchorRow.dataset.sourceStart)===offsets[0]);
        pendingSelection.current = undefined;
        const fragment = document.createDocumentFragment();
        let offset=0;
        const runs=styledRuns.map(value=>{const start=offset;offset+=value.run.text.length;return {...value,start,end:offset};});
        const appendRuns=(parent:DocumentFragment|HTMLElement,start:number,end:number,fontSize=12,width=432)=>{
        for (const {run,appearance,start:runStart,end:runEnd} of runs) {
            if(runEnd<=start || runStart>=end)continue;
            const span = document.createElement('span'), styles = appearance.styles;
            span.textContent = run.text.slice(Math.max(0,start-runStart),Math.min(run.text.length,end-runStart));
            const size=scriptFontSize(run.attributes.Size,fontSize);
            span.style.fontSize=`${size}pt`;span.style.lineHeight='1';
            span.style.tabSize=String(Math.min(4,Math.max(1,Math.floor((width+1e-6)/(size*.6)))));
            if (styles.includes('Bold')) span.style.fontWeight = '700';
            if (styles.includes('Italic')) span.style.fontStyle = 'italic';
            if (styles.includes('Underline')) span.style.textDecoration = 'underline';
            if(appearance.color&&appearance.color!=='#000000')span.style.color=appearance.color;
            if(run.attributes.RevisionID){span.dataset.revisionId=run.attributes.RevisionID;span.title=revisions?.sets.find(s=>s.id===run.attributes.RevisionID)?.name??'Revision '+run.attributes.RevisionID;}
            parent.append(span);
        }
        };
        if(lines?.length){
            lines.forEach(line=>{
                const row=document.createElement('span');row.dataset.draftLine=String(line.page);
                row.dataset.sourceStart=String(line.sourceStart);row.dataset.sourceEnd=String(line.sourceEnd);
                const left=line.x-(line.align==='right'?line.width:line.align==='center'?line.width/2:0);
                const maximum=(line.ascent??9.6)*10/8;
                // Place each row from shared coordinates. Flow heights at sizes
                // such as16pt round to CSS subpixels and otherwise accumulate.
                Object.assign(row.style,{display:'block',position:'absolute',top:`${line.top-lines[0].top}pt`,left:`${left-originX}pt`,height:`${line.lineHeight??12}pt`,fontSize:`${maximum}pt`,lineHeight:`${maximum}pt`,whiteSpace:'pre',textIndent:'0',
                    width:`${line.width}pt`,textAlign:line.align});
                appendRuns(row,line.sourceStart,line.sourceEnd,line.fontSize??12,line.width);
                if(!row.childNodes.length)row.append(document.createElement('br'));
                fragment.append(row);
            });
        }else appendRuns(fragment,0,block.content.length);
        // A trailing text node makes a final newline selectable without a fake
        // character becoming part of the stored screenplay.
        if (!fragment.childNodes.length) fragment.append(document.createTextNode(''));
        if (!lines?.length && block.content.endsWith('\n')) {
            const tail = document.createElement('br'); tail.dataset.richTail = 'true'; fragment.append(tail);
        }
        element.replaceChildren(fragment);
        painted.current=signature;
        if (offsets && document.activeElement === element) {
            select(element, Math.min(offsets[0], block.content.length), Math.min(offsets[1], block.content.length),preferNext);
            if(lines?.length&&offsets[0]===offsets[1]){
                const node=window.getSelection()?.anchorNode;
                const row=(node instanceof Element?node:node?.parentElement)?.closest<HTMLElement>('[data-draft-line]');
                row?.scrollIntoView({block:'nearest',inline:'nearest'});
            }
        }
    };
    useLayoutEffect(paint, [block.content, block.richText,revisions,revisionRanges,lines,originX]);
    useImperativeHandle(ref, () => ({
        get selectionStart() { return selectionOffsets(root.current!)?.[0] ?? 0; },
        get selectionEnd() { return selectionOffsets(root.current!)?.[1] ?? 0; },
        get style() { return root.current!.style; }, get scrollHeight() { return root.current!.scrollHeight; },
        focus() { root.current?.focus(); }, getBoundingClientRect() { return root.current!.getBoundingClientRect(); },
        setSelectionRange(start, end) { if (root.current) select(root.current, start, end); },
    }));
    const insert = (text: string) => {
        const element = root.current; if (!element || readOnly || composing.current) return;
        const [start, end] = selectionOffsets(element) ?? [block.content.length, block.content.length];
        pendingSelection.current = [start + text.length, start + text.length];
        const selection=window.getSelection();
        const range = selection?.rangeCount?selection.getRangeAt(0):undefined;
        if (range && element.contains(range.commonAncestorContainer)) {
            range.deleteContents(); const node = document.createTextNode(text); range.insertNode(node);
            range.setStartAfter(node); range.collapse(true);
            const selection = window.getSelection(); selection?.removeAllRanges(); selection?.addRange(range);
        }
        const value=block.content.slice(0,start)+text+block.content.slice(end);
        inputRange.current=undefined;
        if(value===block.content){painted.current='';paint();return;}
        lastEmitted.current=value;
        onChange(value,observedTextEdit(block.content,value,start,end));
    };
    return <><div ref={root} role="textbox" aria-label={ariaLabel ?? block.type.replace('-', ' ')} aria-multiline="true"
        aria-readonly={readOnly} contentEditable={!readOnly} suppressContentEditableWarning
        className={className} style={{whiteSpace: 'pre-wrap', minHeight: '1em', overflowWrap: 'break-word',...style,...(lines?.length?{textIndent:0,transition:'none',position:'relative',minHeight:0,height:`${lines.at(-1)!.top-lines[0].top+(lines.at(-1)!.lineHeight??12)}pt`}:{})}}
        data-placeholder={placeholder} onFocus={onFocus} onBlur={onBlur}
        aria-autocomplete={completion?"list":undefined} aria-controls={completion?.id}
        aria-expanded={completion?true:undefined} aria-activedescendant={completion?`${completion.id}-${completion.index}`:undefined}
        onInput={event => { if (!readOnly && !composing.current && !(event.nativeEvent as InputEvent).isComposing) emitChange(); }}
        onCompositionStart={() => {const element=root.current;const offsets=element&&selectionOffsets(element);compositionRange.current=element&&offsets?{before:element.textContent??'',start:offsets[0],end:offsets[1]}:undefined;inputRange.current=undefined;composing.current = true;}}
        onCompositionEnd={() => {composing.current = false;const range=compositionRange.current;compositionRange.current=undefined;emitChange(range);}}
        onKeyDown={event => {
            // Let the IME own its keys without triggering paragraph commands,
            // global undo, or deletion against the last committed text.
            if(composing.current||event.nativeEvent.isComposing||event.nativeEvent.keyCode===229){event.stopPropagation();return;}
            if(lines?.length&&!readOnly&&!event.nativeEvent.isComposing&&!event.ctrlKey&&!event.metaKey&&!event.altKey&&['Backspace','Delete'].includes(event.key)){
                const element=root.current,offsets=element&&selectionOffsets(element);
                if(element&&offsets){
                    let [start,end]=offsets;
                    if(start===end){
                        if(event.key==='Backspace'&&start>0){const characters=Array.from(block.content.slice(0,start));start-=characters.at(-1)!.length;}
                        else if(event.key==='Delete'&&end<block.content.length)end+=String.fromCodePoint(block.content.codePointAt(end)!).length;
                    }
                    if(start!==end){event.preventDefault();select(element,start,end);insert('');return;}
                }
            }
            if (!readOnly && !event.nativeEvent.isComposing && event.key === 'Enter' && event.shiftKey) {
                event.preventDefault(); insert('\n'); return;
            }
            onKeyDown(event);
        }}
        onPaste={event => {event.preventDefault(); insert(event.clipboardData.getData('text/plain').replace(/\r\n?/g, '\n'));}}
        onCopy={event=>{if(composing.current)return;const offsets=root.current&&selectionOffsets(root.current);if(lines&&offsets){event.preventDefault();event.clipboardData.setData('text/plain',block.content.slice(...offsets));}}}
        onCut={event=>{if(composing.current){event.preventDefault();return;}const offsets=root.current&&selectionOffsets(root.current);if(lines&&offsets&&!readOnly){event.preventDefault();event.clipboardData.setData('text/plain',block.content.slice(...offsets));insert('');}}}
        onDrop={event => event.preventDefault()}
        onBeforeInput={event => {
            const input = event.nativeEvent as InputEvent;
            if (input.inputType?.startsWith('format')) event.preventDefault();
        }} /><ScriptCompletionGhost anchor={root} text={block.content} suffix={suggestedSuffix} composing={composing} readOnly={readOnly}/></>;
});
